import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Canvas } from "fabric";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { CategoryTabs, categories } from "./layers/CategoryTabs";
import { SearchHeader } from "./layers/SearchHeader";
import { ElementsGrid, GridSkeleton } from "./layers/ElementsGrid";
import { useLayerImages } from "./layers/useLayerImages";
import { LayerImage, ScenarioFabricImage, DisplayLayerInfo } from "./layers/types";
import { FabricImage } from "fabric";
import { supabase } from "@/integrations/supabase/client";
import { usePremiumFeatures } from "@/hooks/use-premium-features";
import { CustomCanvas } from "@/types/fabric";
import { ScenarioType } from "@/types/analysis";
import { useIsMobile } from "@/hooks/use-mobile";
import { LayerPanel } from "./layers/LayerPanel";

interface LayersSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  canvas: Canvas | null;
  onToolChange: (tool: string) => void;
  onResetAnalysis?: () => void;
  // Layer management props
  layers: DisplayLayerInfo[];
  toggleLayerVisibility: (layerId: string) => void;
  renameLayer: (layerId: string, newName: string) => void;
  bringLayerForward: (layerId: string) => void;
  sendLayerBackward: (layerId: string) => void;
}

export function LayersSidebar({
  isOpen,
  onClose,
  canvas,
  onToolChange,
  onResetAnalysis,
  // Layer management props
  layers,
  toggleLayerVisibility,
  renameLayer,
  bringLayerForward,
  sendLayerBackward
}: LayersSidebarProps) {
  const [activeCategory, setActiveCategory] = useState<LayerImage['category']>('scenarios');
  const [searchQuery, setSearchQuery] = useState('');
  const { data: layerImages, isLoading } = useLayerImages();
  const { isAuthenticated, handlePremiumFeature } = usePremiumFeatures();
  const isMobile = useIsMobile();

  const filteredImages = layerImages?.filter(image => 
    image.category === activeCategory &&
    (searchQuery === '' || 
     image.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
     image.description?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleImageClick = async (layer: LayerImage) => {
    const requiresLogin = !layer.title.toLowerCase().includes('bilobed');
    
    if (!isAuthenticated && requiresLogin) {
      const handled = handlePremiumFeature('elements');
      if (!handled) return;
    }

    if (!canvas) return;

    if (layer.category === 'scenarios') {
      const existingScenarios = canvas.getObjects().filter((obj) => {
        const scenarioObj = obj as ScenarioFabricImage;
        return scenarioObj.scenario_type;
      });

      if (existingScenarios.length > 0) {
        toast({
          variant: "destructive",
          title: "Scenario Limit Reached",
          description: "Only one scenario can be active at a time. Please remove the existing scenario first.",
        });
        return;
      }
      
      if (onResetAnalysis) {
        onResetAnalysis();
      }
    }

    try {
      console.log('Loading layer image:', layer);
      
      const { data: { publicUrl } } = supabase.storage
        .from('layer-images')
        .getPublicUrl(layer.image_path);

      console.log('Image public URL:', publicUrl);
      
      const img = await new Promise<ScenarioFabricImage>((resolve, reject) => {
        FabricImage.fromURL(publicUrl, {
          crossOrigin: 'anonymous'
        }).then((img) => {
          const maxDim = 500;
          if (img.width && img.height) {
            if (img.width > maxDim || img.height > maxDim) {
              const scale = maxDim / Math.max(img.width, img.height);
              img.scale(scale);
            }
          }

          img.set({
            left: (canvas.width || 0) / 2 - ((img.width || 0) * (img.scaleX || 1)) / 2,
            top: (canvas.height || 0) / 2 - ((img.height || 0) * (img.scaleY || 1)) / 2,
            rx: 8,
            ry: 8
          });

          if (layer.category === 'scenarios' && layer.scenario_type) {
            const scenarioType = layer.scenario_type as ScenarioType;
            (img as ScenarioFabricImage).scenario_type = scenarioType;
            
            (canvas as CustomCanvas).data = {
              ...(canvas as CustomCanvas).data,
              scenarioType,
              procedureType: scenarioType
            };
          }

          resolve(img as ScenarioFabricImage);
        }).catch(reject);
      });

      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      onToolChange("transform");

      toast({
        title: layer.category === 'scenarios' ? "Scenario Added" : "Layer Added",
        description: `Added ${layer.title} to your canvas.`,
      });
    } catch (error) {
      console.error('Error adding layer image:', error);
      toast({
        variant: "destructive",
        title: "Error adding layer",
        description: "There was a problem adding the layer to your canvas.",
      });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent 
        side="left" 
        className={cn(
          "w-[90vw] sm:w-[600px] h-[90vh] p-0",
          "fixed top-1/2 -translate-y-1/2 left-6",
          "max-h-[80vh]",
          "border-r-0 bg-background/95 backdrop-blur-lg",
          "rounded-xl shadow-lg",
          "!ml-0" // Remove margin from left side
        )}
      >
        <div className="flex flex-col h-full">
          <SearchHeader 
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />

          <CategoryTabs 
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />

          {activeCategory === 'scenarios' && (
            <div className="px-4 py-2 bg-secondary/50 border-b border-border/50">
              <p className="text-xs text-primary/90">
                {categories.find(c => c.id === 'scenarios')?.description}
              </p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              {isLoading ? (
                <GridSkeleton />
              ) : (
                <ElementsGrid 
                  images={filteredImages || []}
                  onImageClick={handleImageClick}
                  isAuthenticated={isAuthenticated}
                />
              )}
            </div>
            
            {layers.length > 0 && (
              <div className="border-t border-border/50 pt-2">
                <h3 className="text-xs font-medium text-muted-foreground px-4 py-2">Current Layers</h3>
                <LayerPanel 
                  layers={layers}
                  onToggleVisibility={toggleLayerVisibility}
                  onRename={renameLayer}
                  onBringForward={bringLayerForward}
                  onSendBackward={sendLayerBackward}
                  className="px-2"
                  compact
                />
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
